import React from 'react';
import { calculateLevel } from '../utils/badgeSystem';

/**
 * BadgeAvatar - User avatar component that displays a badge-themed icon based on user's level
 * This replaces the generic profile picture with a badge-level indicator
 */
const BadgeAvatar = ({ commentCount = 0, size = 'md', userName = '', showLevel = false }) => {
  const levelData = calculateLevel(commentCount);
  const { level, badgeInfo } = levelData;

  const sizeClasses = {
    xs: { container: 'w-6 h-6', text: 'text-xs', icon: 'text-xs', level: 'text-[8px]' },
    sm: { container: 'w-8 h-8', text: 'text-sm', icon: 'text-sm', level: 'text-[10px]' },
    md: { container: 'w-10 h-10', text: 'text-base', icon: 'text-base', level: 'text-xs' },
    lg: { container: 'w-12 h-12', text: 'text-lg', icon: 'text-lg', level: 'text-xs' },
    xl: { container: 'w-16 h-16', text: 'text-2xl', icon: 'text-2xl', level: 'text-sm' }
  };

  const sizes = sizeClasses[size] || sizeClasses.md;
  
  // Get initials from userName
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const initials = getInitials(userName);

  return (
    <div className="relative inline-flex items-center justify-center group">
      {/* Main Avatar Circle with Badge Color */}
      <div
        className={`${sizes.container} rounded-full bg-gradient-to-br ${badgeInfo.color} flex items-center justify-center shadow-md ring-2 ring-white transition-transform duration-200 group-hover:scale-110`}
        title={`${badgeInfo.name} - Level ${level}`}
      >
        {/* Badge Icon */}
        <span className={`${sizes.icon}`}>{badgeInfo.icon}</span>
      </div>

      {/* Level Badge Overlay */}
      {showLevel && (
        <div className="absolute -bottom-1 -right-1 bg-white rounded-full px-1.5 py-0.5 shadow-sm border border-gray-200">
          <span className={`${sizes.level} font-bold ${badgeInfo.textColor}`}>
            {level}
          </span>
        </div>
      )}

      {/* Tooltip on Hover */}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-50 pointer-events-none">
        <div className={`${badgeInfo.bgColor} ${badgeInfo.borderColor} border-2 rounded-lg px-3 py-2 shadow-xl whitespace-nowrap`}>
          <p className={`font-bold text-sm ${badgeInfo.textColor}`}>
            {badgeInfo.name}
          </p>
          <p className="text-xs text-gray-600">Level {level}</p>
          <p className="text-xs text-gray-500 italic">{badgeInfo.description}</p>
        </div>
      </div>
    </div>
  );
};

export default BadgeAvatar;
